import { TriggerPoint } from "pages/survey/surveyService";
import { useEffect, useRef, useState } from "react";

export const useExitIntentHandler = (exitSurveySessionkey: string, isSurveyDisplayableStepcode: boolean) => {
  const [exitIntent, setExitIntent] = useState(false);
  const [triggerPoint, setTriggerPoint] = useState<TriggerPoint | null>(null);
  const nextUrlRef = useRef<string | null>(null);

  const checkIsSurveyDisplayed = () => {
    return sessionStorage.getItem(exitSurveySessionkey) === "true";
  };

  // Handle exit intent on mouse leaving the viewport
  useEffect(() => {
    const handleMouseOut = (event: MouseEvent) => {
      if (checkIsSurveyDisplayed()) {
        return;
      }

      if (event.clientY <= 10 && event.relatedTarget === null && isSurveyDisplayableStepcode) {
        setTriggerPoint(TriggerPoint.MouseOut);
        setExitIntent(true);
      }
    };

    document.addEventListener("mouseout", handleMouseOut);
    return () => document.removeEventListener("mouseout", handleMouseOut);
  }, [isSurveyDisplayableStepcode]);

  // Handle exit intent on browser back button click
  useEffect(() => {
    if (checkIsSurveyDisplayed()) {
      return;
    }

    // adds a fake entry to browser history to block back button navigation one time
    globalThis.history.pushState(null, "", globalThis.location.href);

    const handlePopState = () => {
      setTriggerPoint(TriggerPoint.BrowserBack);
      setExitIntent(true);
    };

    globalThis.addEventListener("popstate", handlePopState);

    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [isSurveyDisplayableStepcode]);

  // Handle exit intent when user tries to navigate within the application
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (checkIsSurveyDisplayed()) {
        return;
      }

      const target = event.target as HTMLElement;
      const anchor = target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http")) return;
      nextUrlRef.current = href;

      // prevent navigation
      if (isSurveyDisplayableStepcode) {
        event.preventDefault();
        event.stopPropagation();
        setTriggerPoint(TriggerPoint.NavigateAway);
        setExitIntent(true);
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => document.removeEventListener("click", handleClick, true);
  }, [isSurveyDisplayableStepcode]);

  return { exitIntent, triggerPoint, nextUrl: nextUrlRef.current };
};
